import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import { parseTableParams } from '../../../utils/Utils';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';

const Container = styled.div``;

const directionOptions = (t) => [
  { id: 'entry', name: t('gate.entry') },
  { id: 'exit', name: t('gate.exit') },
  { id: 'bidirectional', name: t('gate.bidirectional') },
];

export default function BuildingGatesTab({ buildingId }) {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setFetching(true);
    api.get(`/v1/gates?buildingId=${buildingId}&pageSize=500`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [buildingId, queryParams, refreshTrigger]);

  const directionLabel = useCallback((dir) => {
    const map = { entry: t('gate.entry'), exit: t('gate.exit'), bidirectional: t('gate.bidirectional') };
    return map[dir] || dir || '-';
  }, [t]);

  const baseColumns = useMemo(() => [
    {
      name: 'name', label: t('gate.name'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'direction', label: t('gate.direction'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (value) => directionLabel(value),
      },
    },
  ], [t, directionLabel]);

  const handleRemoveClick = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, id: row.id, name: row.name });
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await api.delete(`/v1/gates/${deleteConfirm.id}`);
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setDeleteConfirm({ isOpen: false, id: null, name: '' });
    setDeleting(false);
  }, [deleteConfirm.id]);

  const actionOptions = useMemo(() => [
    { type: 'delete', label: t('gate.delete'), onClick: handleRemoveClick },
  ], [t, handleRemoveClick]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleSave = async (formData) => {
    try {
      await api.post('/v1/gates', { ...formData, buildingId });
      setDrawerOpen(false);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch { return false; }
  };

  const schema = useMemo(() => [
    { name: 'name', label: t('gate.name'), type: 'text', grid: 12, required: true },
    {
      name: 'direction', label: t('gate.direction'), type: 'select', grid: 12, required: true,
      options: directionOptions(t), idKey: 'id', labelKey: 'name',
    },
    { name: 'isMainEntrance', label: t('gate.isMainEntrance'), type: 'checkbox', grid: 12 },
    // AKM POC §5.10 — puantaja sayılan kapı (binada işaretli kapı varsa yalnız onlardan geçişler sayılır).
    {
      name: 'countsForTimesheet', label: t('gate.countsForTimesheet'), type: 'checkbox', grid: 12,
      description: t('gate.countsForTimesheetHint'),
    },
    { name: 'description', label: t('gate.description'), type: 'text', grid: 12 },
  ], [t]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('gate.deleteTitle')}
        message={t('gate.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('gate.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord
          newButtonLabel={t('gate.new')}
          onNewButtonClick={() => setDrawerOpen(true)}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('gate.new')}
        schema={schema}
        onSave={handleSave}
      />
    </Container>
  );
}
